// A caller address the IP allowlist must never be widened to: loopback, a private network, link-local or unique
// local. When the token endpoint reports such an address, the real caller is hidden behind this backoffice's own
// reverse proxy (e.g. Caddy on the same host, reached over `::1`) that
// `fawkes.api.service-account-token.trusted-proxies` does not trust yet. Allowing that address would let every
// caller through the proxy pass the IP check (ACC, 2026-09-14).

function ipv4Internal(ip: string): boolean | null {
  const m = ip.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!m) return null;
  const [a, b] = [Number(m[1]), Number(m[2])];
  if ([a, b, Number(m[3]), Number(m[4])].some((n) => n > 255)) return null;
  return (
    a === 0 || // "this network"
    a === 10 ||
    a === 127 ||
    (a === 169 && b === 254) ||
    (a === 172 && b >= 16 && b <= 31) ||
    (a === 192 && b === 168)
  );
}

function ipv6Groups(ip: string): number[] | null {
  const address = ip.split("%")[0]; // drop a zone id (fe80::1%eth0)
  const halves = address.split("::");
  if (halves.length > 2) return null;
  const head = halves[0] ? halves[0].split(":") : [];
  const tail = halves.length === 2 && halves[1] ? halves[1].split(":") : [];
  const fill = halves.length === 2 ? 8 - head.length - tail.length : 0;
  if (fill < 0) return null;
  const groups = [...head, ...Array<string>(fill).fill("0"), ...tail];
  if (groups.length !== 8 || groups.some((g) => !/^[0-9a-f]{1,4}$/.test(g))) return null;
  return groups.map((g) => parseInt(g, 16));
}

export function isInternalAddress(ip: string | null | undefined): boolean {
  if (!ip) return false;
  const value = ip.trim().toLowerCase();

  const v4 = ipv4Internal(value);
  if (v4 !== null) return v4;
  if (!value.includes(":")) return false;

  // IPv4-mapped / embedded (::ffff:10.0.0.1): judge the IPv4 part.
  if (value.includes(".")) {
    const embedded = ipv4Internal(value.slice(value.lastIndexOf(":") + 1));
    return embedded ?? false;
  }

  const groups = ipv6Groups(value);
  if (!groups) return false;
  const allZeroBeforeLast = groups.slice(0, 7).every((g) => g === 0);
  if (allZeroBeforeLast && (groups[7] === 1 || groups[7] === 0)) return true; // ::1 loopback, :: unspecified
  if ((groups[0] & 0xfe00) === 0xfc00) return true; // fc00::/7 unique local
  if ((groups[0] & 0xffc0) === 0xfe80) return true; // fe80::/10 link-local
  return false;
}
